/**
 * Error handling utilities for regional store functionality
 * Provides graceful fallbacks and error boundaries for region-related operations
 */

import { STORE_REGIONS, getRegionByStoreValue, getRegionDisplayName } from '~/constants/regions';

export interface RegionErrorBoundary {
  hasError: boolean;
  error?: Error;
  fallbackRegion: string;
}

/**
 * Safe wrapper for getRegionByStoreValue with error handling
 */
export const safeGetRegionByStoreValue = (storeValue: string | null | undefined): string | null => {
  try {
    if (!storeValue || typeof storeValue !== 'string') {
      console.warn('[Region] Invalid store value provided:', storeValue);
      return null;
    }

    const region = getRegionByStoreValue(storeValue);

    if (!region) {
      console.warn('[Region] Store value not found in any region:', storeValue);
      // Log available store values for debugging
      const availableStores = Object.values(STORE_REGIONS)
        .flatMap((region) => region.stores.map((store) => store.storeValue))
        .filter(Boolean);
      console.warn('[Region] Available store values:', availableStores);
    }

    return region;
  } catch (error) {
    console.error('[Region] Error getting region for store:', storeValue, error);
    return null;
  }
};

/**
 * Safe wrapper for getRegionDisplayName with fallback
 */
export const safeGetRegionDisplayName = (regionKey: string | null | undefined): string => {
  try {
    if (!regionKey || typeof regionKey !== 'string') {
      return 'Unknown Region';
    }

    const displayName = getRegionDisplayName(regionKey);
    return displayName || regionKey; // Fallback to key if display name not found
  } catch (error) {
    console.error('[Region] Error getting display name for region:', regionKey, error);
    return 'Unknown Region';
  }
};

/**
 * Validates that all required regions and stores are properly configured
 */
export const validateRegionData = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  try {
    // Check if STORE_REGIONS is defined and not empty
    if (!STORE_REGIONS || Object.keys(STORE_REGIONS).length === 0) {
      errors.push('STORE_REGIONS is empty or undefined');
      return { isValid: false, errors };
    }

    // Validate each region
    Object.entries(STORE_REGIONS).forEach(([regionKey, regionData]) => {
      if (!regionData.displayName) {
        errors.push(`Region ${regionKey} missing displayName`);
      }

      if (!regionData.stores || regionData.stores.length === 0) {
        errors.push(`Region ${regionKey} has no stores`);
      }

      // Validate each store in the region
      regionData.stores.forEach((store, index) => {
        if (!store.name) {
          errors.push(`Region ${regionKey}, store ${index}: missing name`);
        }
        if (!store.link) {
          errors.push(`Region ${regionKey}, store ${index}: missing link`);
        }
        if (!store.storeValue) {
          errors.push(`Region ${regionKey}, store ${index}: missing storeValue`);
        }
      });
    });

    // Check for duplicate store values
    const allStoreValues = Object.values(STORE_REGIONS)
      .flatMap((region) => region.stores.map((store) => store.storeValue))
      .filter(Boolean);

    const duplicates = allStoreValues.filter(
      (value, index) => allStoreValues.indexOf(value) !== index,
    );

    if (duplicates.length > 0) {
      errors.push(`Duplicate store values found: ${duplicates.join(', ')}`);
    }
  } catch (error) {
    errors.push(`Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Development-only function to log region data validation
 */
export const logRegionValidation = () => {
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    const validation = validateRegionData();

    if (validation.isValid) {
      console.log('✅ [Region] All region data is valid');
    } else {
      console.error('❌ [Region] Validation errors:', validation.errors);
    }

    // Log summary stats
    const regionCount = Object.keys(STORE_REGIONS).length;
    const storeCount = Object.values(STORE_REGIONS).reduce(
      (total, region) => total + region.stores.length,
      0,
    );

    console.log(`📊 [Region] Summary: ${regionCount} regions, ${storeCount} stores`);
  }
};

/**
 * Error boundary state for region-related components
 */
export const createRegionErrorBoundary = (): RegionErrorBoundary => ({
  hasError: false,
  error: undefined,
  fallbackRegion: 'los-angeles', // Default to largest region
});

/**
 * Safe region prioritization with error handling
 */
export const safeRegionPrioritization = (
  allRegions: Array<[string, any]>,
  userRegionKey: string | null | undefined,
): Array<[string, any]> => {
  try {
    if (!userRegionKey || !Array.isArray(allRegions)) {
      return allRegions || [];
    }

    const userRegionExists = allRegions.some(([key]) => key === userRegionKey);

    if (!userRegionExists) {
      console.warn('[Region] User region not found in available regions:', userRegionKey);
      return allRegions;
    }

    return [
      ...allRegions.filter(([key]) => key === userRegionKey),
      ...allRegions.filter(([key]) => key !== userRegionKey),
    ];
  } catch (error) {
    console.error('[Region] Error in region prioritization:', error);
    return allRegions || [];
  }
};
